import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import Modal from "../components/Modal";
import ScanCopyUploadModal from "../components/ScanCopyUploadModal";
import ExamReportGenerator from "../components/ExamReportGenerator";
import {
  UserGroupIcon,
  BookOpenIcon,
  CloudArrowUpIcon,
  UsersIcon,
  EyeIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { toastSuccess, toastError, toastInfo } from "../utils/hotToast";
// AllExaminerDetailsModal moved to a full page: /admin/examiners

export default function AdminPanel() {
  // Core data states
  const [users, setUsers] = useState([]);
  const [exams, setExams] = useState([]);
  const [examSummaries, setExamSummaries] = useState([]); // Optimized exam summaries with copy counts
  const [queries, setQueries] = useState([]);
  const [availableExaminers, setAvailableExaminers] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [copiesForModals, setCopiesForModals] = useState([]); // Copies loaded on demand for modals

  // Exam selection / bulk delete states
  const [selectedExamIds, setSelectedExamIds] = useState([]);
  const [selectAllExams, setSelectAllExams] = useState(false);
  const [isDeletingExams, setIsDeletingExams] = useState(false);

  // Modal visibility states
  const [isUsersModalOpen, setIsUsersModalOpen] = useState(false);
  const [isCreateExamModalOpen, setIsCreateExamModalOpen] = useState(false);
  const [
    isAssignExaminersToExamModalOpen,
    setIsAssignExaminersToExamModalOpen,
  ] = useState(false);
  const [isScanUploadModalOpen, setIsScanUploadModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [selectedExamForReport, setSelectedExamForReport] = useState(null);
  const [isEditExamModalOpen, setIsEditExamModalOpen] = useState(false);
  const [selectedExamForEdit, setSelectedExamForEdit] = useState(null);
  const [editExamTitle, setEditExamTitle] = useState("");
  const [editExamTotalMarks, setEditExamTotalMarks] = useState("");

  // Loading state for assigning examiners
  const [isAssigning, setIsAssigning] = useState(false);
  const [isUpdatingExam, setIsUpdatingExam] = useState(false);

  // Form input states
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("student");
  const [newUserGender, setNewUserGender] = useState("");
  const [newUserBatch, setNewUserBatch] = useState("");
  const [newUserDepartment, setNewUserDepartment] = useState("");
  const [newUserAadhar, setNewUserAadhar] = useState("");
  const [newUserPan, setNewUserPan] = useState("");
  const [newUserBankAccount, setNewUserBankAccount] = useState("");
  const [newExamTitle, setNewExamTitle] = useState("");
  const [newExamCourse, setNewExamCourse] = useState("");
  const [newExamExamType, setNewExamExamType] = useState("");
  const [newExamDate, setNewExamDate] = useState("");
  const [newExamTotalMarks, setNewExamTotalMarks] = useState("");
  const [newExamSerialStart, setNewExamSerialStart] = useState("");
  const [newExamSerialEnd, setNewExamSerialEnd] = useState("");

  const [newExamFiles, setNewExamFiles] = useState([]);
  const [newExamFileType, setNewExamFileType] = useState(null);

  const [
    selectedExamForExaminerAssignment,
    setSelectedExamForExaminerAssignment,
  ] = useState(null);
  const [selectedExaminerIds, setSelectedExaminerIds] = useState([]);
  const [examinerCopyCounts, setExaminerCopyCounts] = useState({});
  const [isFetchingExamCopies, setIsFetchingExamCopies] = useState(false);
  const [unlinkingExaminerId, setUnlinkingExaminerId] = useState(null);
  const [loadingReportExamId, setLoadingReportExamId] = useState(null);
  const [markingCompletedExamId, setMarkingCompletedExamId] = useState(null);

  // Search and filter states
  const [examSearchTerm, setExamSearchTerm] = useState("");
  const [assignExaminerSearchTerm, setAssignExaminerSearchTerm] = useState("");
  const [activeUserTab, setActiveUserTab] = useState("all");
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [activeStudentBatchTab, setActiveStudentBatchTab] = useState("all");

  // User deletion management states
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectAllInTab, setSelectAllInTab] = useState(false);
  const [deleteAllBatches, setDeleteAllBatches] = useState(false);
  const [deleteCurrentBatch, setDeleteCurrentBatch] = useState(false);
  const [isDeletingUsers, setIsDeletingUsers] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isCreatingExam, setIsCreatingExam] = useState(false);

  // Toasts provided globally via react-hot-toast (use toastSuccess/toastError/toastInfo)

  const navigate = useNavigate();

  // Use toastSuccess/toastError/toastInfo from utils/hotToast for notifications

  // Fetch all initial data for the admin panel
  const fetchInitialData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const [usersRes, summariesRes, examinersRes, queriesRes] =
        await Promise.all([
          api.get("/admin/users"),
          api.get("/admin/exam-summaries"), // Optimized endpoint with aggregated data
          api.get("/admin/examiners"),
          api.get("/admin/queries"),
        ]);
      setUsers(usersRes.data);
      setExamSummaries(summariesRes.data);
      // Extract exam basic info from summaries for compatibility
      setExams(summariesRes.data);
      setAvailableExaminers(examinersRes.data);
      setQueries(queriesRes.data);
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toastError("Failed to load initial data.");
    } finally {
      setIsLoadingData(false);
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Reset selections when user switches tabs or batches
  useEffect(() => {
    setSelectedUserIds([]);
    setSelectAllInTab(false);
    setDeleteAllBatches(false);
    setDeleteCurrentBatch(false);
  }, [activeUserTab, activeStudentBatchTab]);

  // Get unique student batches for filter dropdown
  const uniqueBatches = useMemo(() => {
    const batches = new Set(
      users
        .filter((user) => user.role === "student" && user.batch)
        .map((user) => user.batch)
    );
    return ["all", ...Array.from(batches).sort()];
  }, [users]);

  // Get student batches only (without 'all') for exam creation dropdown
  const studentBatchesForExam = useMemo(() => {
    const batches = new Set(
      users
        .filter((user) => user.role === "student" && user.batch)
        .map((user) => user.batch)
    );
    return Array.from(batches).sort();
  }, [users]);

  // Add new user to the system
  const handleAddUser = async () => {
    try {
      const userData = {
        name: newUserName,
        email: newUserEmail,
        role: newUserRole,
        gender: newUserGender,
      };
      if (newUserRole === "student") {
        userData.batch = newUserBatch;
      }
      if (newUserRole === "examiner") {
        userData.department = newUserDepartment;
        userData.aadharCard = newUserAadhar;
        userData.panCard = newUserPan;
        userData.bankAccount = newUserBankAccount;
      }
      await api.post("/admin/users", userData);
      toastSuccess("User added successfully!");
      setNewUserName("");
      setNewUserEmail("");
      setNewUserRole("student");
      setNewUserGender("");
      setNewUserBatch("");
      setNewUserDepartment("");
      setNewUserAadhar("");
      setNewUserPan("");
      setNewUserBankAccount("");
      fetchInitialData();
    } catch (err) {
      toastError(`Error adding user: ${err.response?.data?.message || err.message}`);
    }
  };

  // Create new exam with question paper upload
  const handleCreateExam = async (e) => {
    e.preventDefault();
    if (!newExamTitle || newExamFiles.length === 0) {
      toastError("Please provide a title and select a file(s) for the exam.");
      return;
    }

    if (!newExamCourse) {
      toastError("Please select a batch for the exam.");
      return;
    }

    // Validate serial range if both fields are filled
    if (newExamSerialStart || newExamSerialEnd) {
      if (!newExamSerialStart || !newExamSerialEnd) {
        toastError("Please provide both start and end serial numbers, or leave both empty.");
        return;
      }
      if (parseInt(newExamSerialStart) >= parseInt(newExamSerialEnd)) {
        toastError("Serial range start must be less than end (e.g. Start: 13000, End: 13150).");
        return;
      }
    }

    setIsCreatingExam(true);

    const formData = new FormData();
    formData.append("title", newExamTitle);
    formData.append("course", newExamCourse);
    formData.append("examType", newExamExamType);
    formData.append("date", newExamDate);
    formData.append("totalMarks", newExamTotalMarks);
    if (newExamSerialStart && newExamSerialEnd) {
      formData.append("serialStart", newExamSerialStart);
      formData.append("serialEnd", newExamSerialEnd);
    }

    if (newExamFileType === "pdf") {
      formData.append("paper", newExamFiles[0]);
    } else if (newExamFileType === "images") {
      newExamFiles.forEach((file) => {
        formData.append(`images`, file);
      });
    }

    try {
      await api.post("/admin/exams", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      toastSuccess("Exam created successfully!");
      setNewExamTitle("");
      setNewExamCourse("");
      setNewExamExamType("");
      setNewExamDate("");
      setNewExamTotalMarks("");
      setNewExamSerialStart("");
      setNewExamSerialEnd("");
      setNewExamFiles([]);
      setNewExamFileType(null);
      setIsCreateExamModalOpen(false);
      fetchInitialData();
    } catch (err) {
      toastError(`Error creating exam: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsCreatingExam(false);
    }
  };

  // Handle file upload for exam question papers
  // Validates file types: single PDF or multiple images
  const handleExamFileChange = (e) => {
    const files = Array.from(e.target.files);
      if (files.length === 0) {
      setNewExamFiles([]);
      setNewExamFileType(null);
      return;
    }

    if (files[0].type === "application/pdf") {
      if (files.length > 1) {
        toastError("Please select only one PDF file.");
        setNewExamFiles([]);
        setNewExamFileType(null);
        e.target.value = null;
        return;
      }
      setNewExamFiles(files);
      setNewExamFileType("pdf");
    } else if (files[0].type.startsWith("image/")) {
      const allAreImages = files.every((file) =>
        file.type.startsWith("image/")
      );
      if (allAreImages) {
        setNewExamFiles(files);
        setNewExamFileType("images");
      } else {
        toastError("Please select only image files or a single PDF.");
        setNewExamFiles([]);
        setNewExamFileType(null);
        e.target.value = null;
      }
    } else {
      toastError("Unsupported file type. Please upload PDF or images.");
      setNewExamFiles([]);
      setNewExamFileType(null);
      e.target.value = null;
    }
  };

  // Open modal to assign examiners to exam
  const openAssignExaminersModal = (exam) => {
    setSelectedExamForExaminerAssignment(exam);
    setSelectedExaminerIds(
      exam ? exam.assignedExaminers.map((ex) => ex._id) : []
    );
    setIsAssignExaminersToExamModalOpen(true);
  };

  // Handle examiner selection for assignment
  const handleExaminerCheckboxChange = (examinerId) => {
    setSelectedExaminerIds((prev) =>
      prev.includes(examinerId)
        ? prev.filter((id) => id !== examinerId)
        : [...prev, examinerId]
    );
  };

  // Fetch copies for selected exam to compute per-examiner counts
  // This watches both the Assign-Examiners modal selection and the Edit-Exam modal selection
  // Fetch examiner copy counts for selected exam from pre-loaded summaries
  // This watches both the Assign-Examiners modal selection and the Edit-Exam modal selection
  useEffect(() => {
    let cancelled = false;
    const loadExaminerCounts = () => {
      const examForCounts = selectedExamForExaminerAssignment || selectedExamForEdit;
      if (!examForCounts) {
        setExaminerCopyCounts({});
        return;
      }
      setIsFetchingExamCopies(true);
      try {
        // Find summary from pre-loaded data
        const summary = examSummaries.find(s => s._id === examForCounts._id);
        const map = {};
        
        if (summary && summary.examinerStats) {
          summary.examinerStats.forEach((stat) => {
            map[stat._id] = {
              total: stat.assigned || 0,
              evaluated: stat.evaluated || 0
            };
          });
        }
        
        if (!cancelled) setExaminerCopyCounts(map);
      } catch (err) {
        console.error("Failed to load examiner counts:", err);
      } finally {
        if (!cancelled) setIsFetchingExamCopies(false);
      }
    };
    loadExaminerCounts();
    return () => {
      cancelled = true;
    };
  }, [selectedExamForExaminerAssignment, selectedExamForEdit, examSummaries]);

  // Unlink a single examiner from the selected exam (only when they have no copies)
  const handleUnlinkExaminer = async (examinerId) => {
    const activeExam = selectedExamForExaminerAssignment || selectedExamForEdit;
    if (!activeExam) return;
    const confirm = window.confirm("Are you sure you want to unlink this examiner from the selected exam?");
    if (!confirm) return;
    try {
      setUnlinkingExaminerId(examinerId);
      const res = await api.post(`/admin/exams/${activeExam._id}/unassign-examiner`, { examinerId });
      toastSuccess(res.data.message || "Examiner unlinked.");
      // refresh data
      fetchInitialData();
      // update local selected exam object to remove examiner
      if (selectedExamForExaminerAssignment) {
        setSelectedExamForExaminerAssignment((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            assignedExaminers: (prev.assignedExaminers || []).filter((e) => String(e._id || e) !== String(examinerId)),
          };
        });
      }
      if (selectedExamForEdit) {
        setSelectedExamForEdit((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            assignedExaminers: (prev.assignedExaminers || []).filter((e) => String(e._id || e) !== String(examinerId)),
          };
        });
      }
      // refresh counts
      try {
        const resp = await api.get(`/admin/exams/${activeExam._id}/copies`);
        const copiesList = (resp.data && resp.data.copies) || [];
        const map = {};
        copiesList.forEach((c) => {
          if (Array.isArray(c.examiners)) {
            c.examiners.forEach((ex) => {
              const id = ex._id || ex;
              if (!map[id]) map[id] = { total: 0, evaluated: 0 };
              map[id].total += 1;
              if (c.status === "evaluated") map[id].evaluated += 1;
            });
          }
        });
        setExaminerCopyCounts(map);
      } catch (e) {
        /* ignore */
      }
    } catch (err) {
      toastError(err.response?.data?.message || err.message || "Failed to unlink examiner.");
    } finally {
      setUnlinkingExaminerId(null);
    }
  };

  // Assign selected examiners to exam and distribute copies
  const handleAssignExaminersToExam = async () => {
    if (!selectedExamForExaminerAssignment) {
      toastError("Please select an exam.");
      return;
    }
    if (selectedExaminerIds.length === 0) {
      toastError("Please select at least one examiner to assign.");
      return;
    }

    try {
      setIsAssigning(true);
      const res = await api.post(
        `/admin/exams/${selectedExamForExaminerAssignment._id}/assign-examiners`,
        {
          examinerIds: selectedExaminerIds,
        }
      );
      toastSuccess(res.data.message);
      setIsAssignExaminersToExamModalOpen(false);
      fetchInitialData();
    } catch (err) {
      toastError(`Error assigning examiners: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsAssigning(false);
    }
  };

  // Undo assignment of examiners for an exam (clear paper assignments and unassign non-evaluated copies)
  const handleUndoExamAssignment = async (exam) => {
    if (!exam || !exam._id) return;
    const confirm = window.confirm(
      "Are you sure you want to undo examiner assignments for this exam? This will remove assigned examiners and unassign copies that are not yet evaluated."
    );
    if (!confirm) return;

    try {
      const res = await api.post(`/admin/exams/${exam._id}/unassign-examiners`);
      toastSuccess(res.data?.message || "Assignments undone.");
      fetchInitialData();
    } catch (err) {
      toastError(`Error undoing assignments: ${err.response?.data?.message || err.message}`);
    }
  };

  // Handle successful scan upload callback
  const handleScanUploadSuccess = () => {
    toastSuccess("Scanned copy uploaded and registered!");
    setIsScanUploadModalOpen(false);
    fetchInitialData();
  };

  // Fetch all copies for modals that need them
  const fetchCopiesForModals = useCallback(async () => {
    try {
      const res = await api.get('/admin/copies');
      setCopiesForModals(res.data || []);
    } catch (err) {
      console.error('Failed to load copies:', err);
      setCopiesForModals([]);
    }
  }, []);

  // Open scan upload modal and fetch copies
  const handleOpenScanUploadModal = () => {
    setIsScanUploadModalOpen(true);
    fetchCopiesForModals();
  };

  // Open report generator modal
  const handleOpenReportModal = async (exam) => {
    setLoadingReportExamId(exam._id);
    
    // Fetch copies for this exam BEFORE opening the modal
    try {
      const res = await api.get(`/admin/exams/${exam._id}/copies`);
      setCopiesForModals(res.data.copies || []);
      // Update exam with complete data from backend
      setSelectedExamForReport(res.data.exam);
      // Only open modal after data is loaded
      setIsReportModalOpen(true);
    } catch (err) {
      console.error('Failed to load copies for report:', err);
      toastError('Failed to load report data. Please try again.');
    } finally {
      setLoadingReportExamId(null);
    }
  };

  // Open edit exam modal
  const handleOpenEditExamModal = (exam) => {
    setSelectedExamForEdit(exam);
    setEditExamTitle(exam.title || "");
    setEditExamTotalMarks(exam.totalMarks?.toString() || "");
    setIsEditExamModalOpen(true);
  };

  // Handle exam details update (title & total marks)
  const handleUpdateExamTitle = async () => {
    if (!editExamTitle.trim()) {
      toastError("Exam title cannot be empty.");
      return;
    }

    const totalMarksNum = editExamTotalMarks ? parseInt(editExamTotalMarks, 10) : selectedExamForEdit?.totalMarks;
    if (!totalMarksNum || totalMarksNum <= 0) {
      toastError("Total marks must be a positive number.");
      return;
    }

    const noChanges = editExamTitle === selectedExamForEdit?.title && totalMarksNum === selectedExamForEdit?.totalMarks;
    if (noChanges) {
      toastInfo("No changes made to the exam details.");
      setIsEditExamModalOpen(false);
      return;
    }

    try {
      setIsUpdatingExam(true);
      await api.patch(`/admin/exams/${selectedExamForEdit._id}`, {
        title: editExamTitle.trim(),
        totalMarks: totalMarksNum,
      });

      // Update local state
      setExams((prevExams) =>
        prevExams.map((exam) =>
          exam._id === selectedExamForEdit._id
            ? { ...exam, title: editExamTitle.trim(), totalMarks: totalMarksNum }
            : exam
        )
      );

      toastSuccess("Exam details updated successfully!");
      setIsEditExamModalOpen(false);
      setSelectedExamForEdit(null);
      setEditExamTitle("");
      setEditExamTotalMarks("");
    } catch (error) {
      console.error("Error updating exam details:", error);
      toastError(
        error.response?.data?.message || "Failed to update exam details."
      );
    } finally {
      setIsUpdatingExam(false);
    }
  };

  // Calculate exam progress and status summary from pre-computed backend data
  const getExamProgressSummary = useCallback(
    (examId) => {
      const summary = examSummaries.find(s => s._id === examId);
      
      if (!summary || !summary.statusCounts) {
        return { status: "No Copies Uploaded", progress: {} };
      }

      const { statusCounts, examinerStats } = summary;
      const totalCopies = statusCounts.total || 0;
      const totalEvaluated = statusCounts.evaluated || 0;

      if (totalCopies === 0) {
        return { status: "No Copies Uploaded", progress: {} };
      }

      // Convert examinerStats array to progress object
      const progress = {};
      if (examinerStats && Array.isArray(examinerStats)) {
        examinerStats.forEach(stat => {
          progress[stat._id] = {
            name: stat.name || stat.email,
            assigned: stat.assigned || 0,
            evaluated: stat.evaluated || 0,
          };
        });
      }

      let overallStatus = "Pending Assignment";
      if (totalCopies > 0 && Object.keys(progress).length > 0) {
        if (totalEvaluated === totalCopies) {
          overallStatus = "Completed";
        } else if (totalEvaluated > 0) {
          overallStatus = "In Progress";
        } else {
          overallStatus = "Assigned, Not Started";
        }
      } else if (totalCopies > 0) {
        overallStatus = "Copies Uploaded, Unassigned";
      }

      return { status: overallStatus, progress };
    },
    [examSummaries]
  );

  // Filter exams that don't have assigned examiners
  const unassignedExamsForModal = exams.filter(
    (exam) =>
      !exam.evaluationCompleted &&
      (!exam.assignedExaminers || exam.assignedExaminers.length === 0)
  );

  // Filter exams based on search term
  const filteredExams = exams.filter((exam) =>
    exam.title.toLowerCase().includes(examSearchTerm.toLowerCase())
  );

  // Keep active and archived pools separate for easier admin operations
  const filteredActiveExams = filteredExams.filter(
    (exam) => !exam.evaluationCompleted
  );
  const handleMarkEvaluationCompleted = async (exam) => {
    if (!exam?._id) return;
    const confirmClose = window.confirm(
      "Mark this exam as evaluation-completed? Examiners will no longer see its copies, but release-to-students can still be used."
    );
    if (!confirmClose) return;

    setMarkingCompletedExamId(exam._id);
    try {
      await api.patch(`/admin/exams/${exam._id}/mark-evaluation-completed`);
      toastSuccess("Exam moved to Completed Exams pool.");
      setSelectedExamIds((prev) => prev.filter((id) => id !== exam._id));
      setSelectAllExams(false);
      fetchInitialData();
    } catch (err) {
      toastError(
        err.response?.data?.message ||
          "Failed to mark evaluation as completed."
      );
    } finally {
      setMarkingCompletedExamId(null);
    }
  };

  // Handlers for selecting and deleting exams (single + bulk)
  const handleExamCheckboxChange = (examId) => {
    setSelectedExamIds((prev) =>
      prev.includes(examId) ? prev.filter((id) => id !== examId) : [...prev, examId]
    );
  };

  const handleSelectAllExams = (e) => {
    const checked = e.target.checked;
    setSelectAllExams(checked);
    if (checked) {
      setSelectedExamIds(filteredActiveExams.map((ex) => ex._id));
    } else {
      setSelectedExamIds([]);
    }
  };

  const handleDeleteExam = async (examId) => {
    const confirm = window.confirm("Delete this exam and all its copies and queries? This cannot be undone.");
    if (!confirm) return;
    try {
      await api.delete(`/admin/exams/${examId}`);
      toastSuccess("Exam deleted.");
      fetchInitialData();
    } catch (err) {
      toastError(`Error deleting exam: ${err.response?.data?.message || err.message}`);
    }
  };

  const handleDeleteSelectedExams = async () => {
    if (selectedExamIds.length === 0) {
      toastInfo("No exams selected for deletion.");
      return;
    }
    const confirm = window.confirm(`Delete ${selectedExamIds.length} selected exam(s) and their copies/queries? This cannot be undone.`);
    if (!confirm) return;
    setIsDeletingExams(true);
    try {
      await api.delete(`/admin/exams`, { data: { examIds: selectedExamIds } });
      toastSuccess("Selected exams deleted.");
      setSelectedExamIds([]);
      setSelectAllExams(false);
      fetchInitialData();
    } catch (err) {
      toastError(`Error deleting exams: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsDeletingExams(false);
    }
  };

  // Filter users based on role, batch, and search criteria
  const getFilteredUsers = useCallback(
    (role) => {
      let filtered = users;
      if (role !== "all") {
        filtered = users.filter((user) => user.role === role);
      }
      if (activeUserTab === "student" && activeStudentBatchTab !== "all") {
        filtered = filtered.filter(
          (user) => user.batch === activeStudentBatchTab
        );
      }

      if (userSearchTerm) {
        filtered = filtered.filter(
          (user) =>
            user.email.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            user.name.toLowerCase().includes(userSearchTerm.toLowerCase())
        );
      }
      return filtered;
    },
    [users, activeUserTab, activeStudentBatchTab, userSearchTerm]
  );

  // Toggle individual user selection
  const handleUserCheckboxChange = (userId) => {
    setSelectedUserIds((prevSelected) =>
      prevSelected.includes(userId)
        ? prevSelected.filter((id) => id !== userId)
        : [...prevSelected, userId]
    );
  };

  // Toggle select all users in current tab
  const handleSelectAllInTab = (e) => {
    const isChecked = e.target.checked;
    setSelectAllInTab(isChecked);
    if (isChecked) {
      const currentTabUserIds = getFilteredUsers(activeUserTab).map(
        (user) => user._id
      );
      setSelectedUserIds(currentTabUserIds);
    } else {
      setSelectedUserIds([]);
    }
  };

  // Handle bulk delete all student batches option
  const handleDeleteAllBatchesChange = (e) => {
    const isChecked = e.target.checked;
    setDeleteAllBatches(isChecked);
    if (isChecked) {
      setDeleteCurrentBatch(false);
      setSelectedUserIds([]);
      setSelectAllInTab(false);
    }
  };

  // Handle delete current batch option
  const handleDeleteCurrentBatchChange = (e) => {
    const isChecked = e.target.checked;
    setDeleteCurrentBatch(isChecked);
    if (isChecked) {
      setDeleteAllBatches(false);
      setSelectedUserIds([]);
      setSelectAllInTab(false);
    }
  };

  // Prepare user deletion based on selected options
  // Handles individual, batch, or bulk deletions with appropriate confirmation
  const handleDeleteUsers = async () => {
    let userIdsToDelete = [];

    if (deleteAllBatches) {
      // Deleting all students across all batches
      userIdsToDelete = users
        .filter((user) => user.role === "student")
        .map((user) => user._id);
    } else if (deleteCurrentBatch && activeStudentBatchTab !== "all") {
      // Deleting all students in the currently selected batch
      userIdsToDelete = users
        .filter(
          (user) =>
            user.role === "student" && user.batch === activeStudentBatchTab
        )
        .map((user) => user._id);
    } else if (selectAllInTab) {
      // Deleting all users in the current tab/filter
      userIdsToDelete = getFilteredUsers(activeUserTab).map((user) => user._id);
    } else if (selectedUserIds.length > 0) {
      // Deleting individually selected users
      userIdsToDelete = selectedUserIds;
    } else {
      toastInfo("No users selected for deletion.");
      return;
    }

    if (userIdsToDelete.length === 0) {
      toastInfo("No users found to delete based on your selection.");
      return;
    }

    setShowDeleteConfirmModal(true);
  };

  // Execute user deletion after confirmation
  const confirmDeleteUsers = async () => {
    setShowDeleteConfirmModal(false);
    setIsDeletingUsers(true);
    let userIdsToDelete = [];

    if (deleteAllBatches) {
      userIdsToDelete = users
        .filter((user) => user.role === "student")
        .map((user) => user._id);
    } else if (deleteCurrentBatch && activeStudentBatchTab !== "all") {
      userIdsToDelete = users
        .filter(
          (user) =>
            user.role === "student" && user.batch === activeStudentBatchTab
        )
        .map((user) => user._id);
    } else if (selectAllInTab) {
      userIdsToDelete = getFilteredUsers(activeUserTab).map((user) => user._id);
    } else {
      userIdsToDelete = selectedUserIds;
    }

    try {
      const res = await api.delete("/admin/users", {
        data: { userIds: userIdsToDelete },
      });
      toastSuccess(res.data.message);
      fetchInitialData();
      setSelectedUserIds([]);
      setSelectAllInTab(false);
      setDeleteAllBatches(false);
      setDeleteCurrentBatch(false);
    } catch (err) {
      toastError(`Error deleting users: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsDeletingUsers(false);
    }
  };

  // Render users table with selection controls
  const renderUsersTable = (usersToDisplay) => (
    <div className="overflow-x-auto no-scrollbar max-h-60 overflow-y-auto mt-4 rounded-xl border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <input
                type="checkbox"
                className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                checked={
                  selectAllInTab &&
                  selectedUserIds.length === usersToDisplay.length &&
                  usersToDisplay.length > 0
                }
                onChange={handleSelectAllInTab}
                disabled={
                  usersToDisplay.length === 0 ||
                  deleteAllBatches ||
                  deleteCurrentBatch
                }
              />
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Email
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Role
            </th>
            {activeUserTab === "student" && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Batch
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {usersToDisplay.length === 0 ? (
            <tr>
              <td
                colSpan={activeUserTab === "student" ? "5" : "4"}
                className="px-6 py-4 text-center text-gray-500"
              >
                No users found.
              </td>
            </tr>
          ) : (
            usersToDisplay.map((user) => (
              <tr key={user._id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                    checked={selectedUserIds.includes(user._id)}
                    onChange={() => handleUserCheckboxChange(user._id)}
                    disabled={
                      selectAllInTab || deleteAllBatches || deleteCurrentBatch
                    }
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {user.role}
                </td>
                {activeUserTab === "student" && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.batch || "N/A"}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="p-6 lg:p-10 space-y-10 bg-white min-h-screen" style={{fontFamily: 'Dosis, sans-serif'}}>
      {/* Toasts are provided globally via react-hot-toast */}

      <div className="w-full">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2 tracking-tight" style={{fontFamily: 'Dosis, sans-serif'}}>
          Admin Dashboard
        </h1>
        <p className="text-gray-600 text-sm lg:text-base">Manage exams, users, and system operations</p>
      </div>

      <div className="grid grid-cols-6 gap-4 w-full overflow-x-auto">
        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <UserGroupIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Manage Users
            </h2>
          </div>
          <button
            onClick={() => navigate('/admin/users')}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm mt-auto"
          >
            Open ({users.length})
          </button>
        </div>

        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <BookOpenIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Manage Exams
            </h2>
          </div>
          <button
            onClick={() => setIsCreateExamModalOpen(true)}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm mt-auto"
          >
            Create Exam
          </button>
        </div>


        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <CloudArrowUpIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Upload Copy
            </h2>
          </div>
          <button
            onClick={handleOpenScanUploadModal}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm mt-auto"
          >
            Upload
          </button>
        </div>


        
        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <UsersIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Assign Examiners
            </h2>
          </div>
          <button
            onClick={() => openAssignExaminersModal(null)}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm mt-auto"
          >
            Assign
          </button>
        </div>

        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <QuestionMarkCircleIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Student Queries
            </h2>
          </div>
          <button
            onClick={() => navigate('/admin/queries')}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm flex items-center justify-center gap-2 mt-auto"
          >
            <span>Manage</span>
            {queries.filter((q) => q.status === "pending").length > 0 && (
              <span className="px-2 py-0.5 bg-white text-gray-900 rounded-full text-xs font-semibold">
                {queries.filter((q) => q.status === "pending").length}
              </span>
            )}
          </button>
        </div>
        
        <div className="group bg-white p-5 rounded-xl border-2 border-gray-900 hover:border-[#1e3a8a] transition-all duration-300 flex flex-col min-w-[180px]">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="p-3 bg-gray-900 rounded-xl mb-3">
              <ClipboardDocumentListIcon className="h-6 w-6 text-white" />
            </div>
            <h2 className="text-base font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
              Examiner Stats
            </h2>
          </div>
          <button
            onClick={() => navigate('/admin/examiners')}
            className="w-full bg-gray-900 hover:bg-[#1e3a8a] text-white font-medium py-2.5 px-4 rounded-xl transition duration-200 text-sm mt-auto"
          >
            View Stats
          </button>
        </div>
      </div>

      <section className="bg-white p-6 lg:p-8 rounded-2xl border-2 border-gray-900 w-full">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 pb-6 border-b border-gray-300">
          <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily: 'Dosis, sans-serif'}}>
            Exam Overview & Progress
          </h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto">
            <button
              onClick={() => navigate('/admin/completed-exams')}
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl text-white bg-gray-900 hover:bg-[#1e3a8a] transition duration-200"
            >
              Reopen Completed Exams
            </button>
            <div className="relative w-full lg:w-80">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              type="text"
              placeholder="Search exams..."
              className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              value={examSearchTerm}
              onChange={(e) => setExamSearchTerm(e.target.value)}
            />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end mb-4">
          <button
            onClick={handleDeleteSelectedExams}
            disabled={selectedExamIds.length === 0 || isDeletingExams}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-xl text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition duration-200"
          >
            {isDeletingExams ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" /> Deleting...
              </>
            ) : (
              <>
                <TrashIcon className="h-4 w-4 mr-2" /> Delete ({selectedExamIds.length})
              </>
            )}
          </button>
        </div>

        {filteredActiveExams.length === 0 && exams.length > 0 ? (
          <p className="text-gray-600 text-center py-4">
            No active exams found matching your search criteria.
          </p>
        ) : filteredActiveExams.length === 0 && exams.length === 0 ? (
          <p className="text-gray-600 text-center py-4">
            No exams created yet. Create an exam to see its progress.
          </p>
        ) : (
          <div className="overflow-x-auto max-h-[600px] overflow-y-auto rounded-xl border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      checked={selectAllExams}
                      onChange={handleSelectAllExams}
                    />
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Exam Title
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Course
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Exam Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Created At
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Total Copies
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Overall Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Examiner Progress
                  </th>
                      <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredActiveExams.map((exam) => {
                  const { status: overallStatus, progress: examinerProgress } =
                    getExamProgressSummary(exam._id);
                  const summary = examSummaries.find(s => s._id === exam._id);
                  const totalCopiesForExam = summary?.statusCounts?.total || 0;

                  return (
                    <tr
                      key={exam._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                          checked={selectedExamIds.includes(exam._id)}
                          onChange={() => handleExamCheckboxChange(exam._id)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {exam.title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {exam.course || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {exam.date
                          ? new Date(exam.date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {exam.createdAt
                          ? new Date(exam.createdAt).toLocaleString()
                          : "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {totalCopiesForExam}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            overallStatus === "Completed"
                              ? "bg-gray-900 text-white"
                              : overallStatus.includes("Progress") ||
                                overallStatus.includes("Assigned")
                              ? "bg-gray-200 text-gray-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {overallStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {Object.keys(examinerProgress).length > 0 ? (
                          <ul className="list-disc list-inside space-y-0.5">
                            {Object.values(examinerProgress).map((p, idx) => (
                              <li key={idx}>
                                {p.name}: {p.evaluated}/{p.assigned}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          "No examiners assigned or no copies yet."
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <a
                            href={`/admin/exams/${exam._id}`}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 transition duration-200"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" /> View
                          </a>

                          <button
                            onClick={() => handleOpenEditExamModal(exam)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-[#1e3a8a] hover:bg-[#1e40af] transition duration-200"
                            title="Edit Exam Title"
                          >
                            <PencilSquareIcon className="h-4 w-4 mr-1" /> Edit
                          </button>

                          {totalCopiesForExam > 0 && (
                            <button
                              onClick={() => handleOpenReportModal(exam)}
                              disabled={loadingReportExamId === exam._id}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Download Report"
                            >
                              {loadingReportExamId === exam._id ? (
                                <>
                                  <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" /> Loading...
                                </>
                              ) : (
                                <>
                                  <DocumentArrowDownIcon className="h-4 w-4 mr-1" /> Report
                                </>
                              )}
                            </button>
                          )}

                          {overallStatus === "Completed" && !exam.evaluationCompleted && (
                            <button
                              onClick={() => handleMarkEvaluationCompleted(exam)}
                              disabled={markingCompletedExamId === exam._id}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-emerald-700 hover:bg-emerald-800 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Mark Evaluation as Completed"
                            >
                              {markingCompletedExamId === exam._id
                                ? "Marking..."
                                : "Mark Complete"}
                            </button>
                          )}

                          {exam.assignedExaminers && exam.assignedExaminers.length > 0 && totalCopiesForExam > 0 && (
                            <button
                              onClick={() => handleUndoExamAssignment(exam)}
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition duration-200"
                            >
                              Undo
                            </button>
                          )}

                          <button
                            onClick={() => handleDeleteExam(exam._id)}
                            className="inline-flex items-center p-1.5 text-sm font-medium rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition duration-200"
                            title="Delete Exam"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </section>

      <Modal
        isOpen={isUsersModalOpen}
        onClose={() => setIsUsersModalOpen(false)}
        title="Manage Users"
      >
        <h3 className="text-lg font-semibold mb-4 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>Add New User</h3>
        <div className="space-y-3 mb-6 p-5 border border-gray-200 rounded-xl bg-gray-50">
          <input
            type="text"
            placeholder="Name"
            value={newUserName}
            onChange={(e) => setNewUserName(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={newUserEmail}
            onChange={(e) => setNewUserEmail(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            required
          />
          <select
            value={newUserGender}
            onChange={(e) => setNewUserGender(e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            required
          >
            <option value="">Select Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
          <select
            value={newUserRole}
            onChange={(e) => {
              setNewUserRole(e.target.value);
              setNewUserBatch("");
              setNewUserDepartment("");
              setNewUserAadhar("");
              setNewUserPan("");
              setNewUserBankAccount("");
            }}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
          >
            <option value="student">Student</option>
            <option value="examiner">Examiner</option>
            <option value="admin">Admin</option>
          </select>
          {newUserRole === "student" && (
            <input
              type="text"
              placeholder="Batch (e.g., 2023)"
              value={newUserBatch}
              onChange={(e) => setNewUserBatch(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          )}
          {newUserRole === "examiner" && (
            <>
              <input
                type="text"
                placeholder="Department"
                value={newUserDepartment}
                onChange={(e) => setNewUserDepartment(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
              <input
                type="text"
                placeholder="Aadhar Card Number"
                value={newUserAadhar}
                onChange={(e) => setNewUserAadhar(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
              <input
                type="text"
                placeholder="PAN Card Number"
                value={newUserPan}
                onChange={(e) => setNewUserPan(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
              <input
                type="text"
                placeholder="Bank Account Details"
                value={newUserBankAccount}
                onChange={(e) => setNewUserBankAccount(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
            </>
          )}
          <button
            onClick={handleAddUser}
            className="bg-gray-900 text-white py-2.5 px-4 rounded-lg w-full hover:bg-gray-800 transition duration-200 font-medium text-sm"
          >
            Add User
          </button>
        </div>

        <h3 className="text-lg font-semibold mb-4 text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>Existing Users</h3>
        <div className="flex border-b border-gray-200 mb-4 overflow-x-auto no-scrollbar">
          <button
            className={`py-2 px-4 text-sm font-medium transition duration-200 ${
              activeUserTab === "all"
                ? "border-b-2 border-gray-900 text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              setActiveUserTab("all");
              setUserSearchTerm("");
              setActiveStudentBatchTab("all");
            }}
          >
            All ({getFilteredUsers("all").length})
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium transition duration-200 ${
              activeUserTab === "student"
                ? "border-b-2 border-gray-900 text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              setActiveUserTab("student");
              setUserSearchTerm("");
            }}
          >
            Students ({getFilteredUsers("student").length})
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium transition duration-200 ${
              activeUserTab === "examiner"
                ? "border-b-2 border-gray-900 text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              setActiveUserTab("examiner");
              setUserSearchTerm("");
              setActiveStudentBatchTab("all");
            }}
          >
            Examiners ({getFilteredUsers("examiner").length})
          </button>
          <button
            className={`py-2 px-4 text-sm font-medium transition duration-200 ${
              activeUserTab === "admin"
                ? "border-b-2 border-gray-900 text-gray-900"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => {
              setActiveUserTab("admin");
              setUserSearchTerm("");
              setActiveStudentBatchTab("all");
            }}
          >
            Admins ({getFilteredUsers("admin").length})
          </button>
        </div>

        {activeUserTab === "student" && (
          <div className="flex border-b border-gray-200 mb-4 overflow-x-auto no-scrollbar ml-4">
            {uniqueBatches.map((batch) => (
              <button
                key={batch}
                className={`py-2 px-4 text-xs font-medium transition duration-200 ${
                  activeStudentBatchTab === batch
                    ? "border-b-2 border-gray-900 text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => {
                  setActiveStudentBatchTab(batch);
                  setUserSearchTerm("");
                }}
              >
                {batch === "all" ? "All Batches" : `Batch ${batch}`} (
                {
                  users.filter(
                    (user) =>
                      user.role === "student" &&
                      (batch === "all" || user.batch === batch)
                  ).length
                }
                )
              </button>
            ))}
          </div>
        )}

        <div className="mb-4 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="text"
            placeholder={`Search by name or email...`}
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
          />
        </div>

        {renderUsersTable(getFilteredUsers(activeUserTab))}

        <div className="mt-6 pt-4 border-t border-gray-200 space-y-3">
          <h4 className="text-base font-semibold text-gray-900" style={{fontFamily: 'Montserrat, sans-serif'}}>
            Bulk Delete Options
          </h4>
          <div className="flex flex-col space-y-2">
            {activeUserTab === "student" && (
              <>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                    checked={deleteAllBatches}
                    onChange={handleDeleteAllBatchesChange}
                    disabled={
                      selectedUserIds.length > 0 ||
                      selectAllInTab ||
                      (activeStudentBatchTab !== "all" && deleteCurrentBatch)
                    }
                  />
                  <span className="ml-2 text-sm text-gray-700 font-medium">
                    Delete ALL Students (All Batches)
                  </span>
                </label>
                {activeStudentBatchTab !== "all" && (
                  <label className="inline-flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                      checked={deleteCurrentBatch}
                      onChange={handleDeleteCurrentBatchChange}
                      disabled={
                        selectedUserIds.length > 0 ||
                        selectAllInTab ||
                        deleteAllBatches
                      }
                    />
                    <span className="ml-2 text-sm text-gray-700 font-medium">
                      Delete ALL Students in Current Batch (
                      {activeStudentBatchTab})
                    </span>
                  </label>
                )}
              </>
            )}

            <button
              onClick={handleDeleteUsers}
              disabled={
                isDeletingUsers ||
                (selectedUserIds.length === 0 &&
                  !selectAllInTab &&
                  !deleteAllBatches &&
                  !deleteCurrentBatch) ||
                (activeUserTab === "student" &&
                  deleteAllBatches &&
                  users.filter((u) => u.role === "student").length === 0) ||
                (activeUserTab === "student" &&
                  deleteCurrentBatch &&
                  activeStudentBatchTab !== "all" &&
                  users.filter(
                    (u) =>
                      u.role === "student" && u.batch === activeStudentBatchTab
                  ).length === 0) ||
                (selectAllInTab && getFilteredUsers(activeUserTab).length === 0)
              }
              className="mt-4 bg-gray-900 text-white py-2.5 px-4 rounded-xl w-full hover:bg-gray-800 transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
            >
              {isDeletingUsers ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />{" "}
                  Deleting...
                </>
              ) : (
                <>
                  <TrashIcon className="h-5 w-5 mr-2" /> Delete
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showDeleteConfirmModal}
        onClose={() => setShowDeleteConfirmModal(false)}
        title="Confirm Deletion"
      >
        <div className="p-4 text-center">
          <p className="text-base text-gray-700 mb-6">
            Are you absolutely sure you want to delete the selected users? This
            action cannot be undone.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setShowDeleteConfirmModal(false)}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 font-medium transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteUsers}
              className="px-6 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 font-medium transition text-sm"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isCreateExamModalOpen}
        onClose={() => setIsCreateExamModalOpen(false)}
        title="Create New Exam"
      >
        <form onSubmit={handleCreateExam} className="space-y-4 p-2">
          <div>
            <label
              htmlFor="examTitle"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exam Subject
            </label>
            <input
              type="text"
              id="examTitle"
              placeholder="e.g., Mid-Term Exam 2025"
              value={newExamTitle}
              onChange={(e) => setNewExamTitle(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              required
            />
          </div>
          <div>
            <label
              htmlFor="examCourse"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Batch
            </label>
            <select
              id="examCourse"
              value={newExamCourse}
              onChange={(e) => setNewExamCourse(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              required
            >
              <option value="">-- Select Batch --</option>
              {studentBatchesForExam.length === 0 ? (
                <option value="" disabled>
                  No students with batches found
                </option>
              ) : (
                studentBatchesForExam.map((batch) => (
                  <option key={batch} value={batch}>
                    {batch}
                  </option>
                ))
              )}
            </select>
            {studentBatchesForExam.length === 0 && (
              <p className="text-xs text-gray-600 mt-1">
                Please add students with batch numbers first
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="examType"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Exam Type
            </label>
            <input
              type="text"
              id="examType"
              placeholder="e.g., Mid-Term, Final, Quiz"
              value={newExamExamType}
              onChange={(e) => setNewExamExamType(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              required
            />
          </div>
          <div>
            <label
              htmlFor="examDate"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date
            </label>
            <input
              type="date"
              id="examDate"
              value={newExamDate}
              onChange={(e) => setNewExamDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              required
            />
          </div>
          <div>
            <label
              htmlFor="totalMarks"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Total Marks
            </label>
            <input
              type="number"
              id="totalMarks"
              placeholder="e.g., 100"
              value={newExamTotalMarks}
              onChange={(e) => setNewExamTotalMarks(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              required
            />
          </div>
          {/* Serial Number Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Copy Serial Number Range{" "}
              <span className="text-gray-400 font-normal">(Optional)</span>
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Set a range to map physical answer copies (e.g., Start: 13000, End:
              13150 means 150 copies numbered 13001–13150). Each upload will
              require selecting the matching serial number.
            </p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                id="serialStart"
                placeholder="Start (e.g. 13000)"
                value={newExamSerialStart}
                onChange={(e) => setNewExamSerialStart(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
              <span className="text-gray-500 font-medium shrink-0">&ndash;</span>
              <input
                type="number"
                id="serialEnd"
                placeholder="End (e.g. 13150)"
                value={newExamSerialEnd}
                onChange={(e) => setNewExamSerialEnd(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              />
            </div>
            {newExamSerialStart && newExamSerialEnd && parseInt(newExamSerialStart) < parseInt(newExamSerialEnd) && (
              <p className="text-xs text-green-700 mt-1 font-medium">
                {parseInt(newExamSerialEnd) - parseInt(newExamSerialStart)} copies
                ({parseInt(newExamSerialStart) + 1}&ndash;{newExamSerialEnd})
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="examFile"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Question Paper (PDF or Images)
            </label>
            <input
              type="file"
              id="examFile"
              accept=".pdf, .jpg, .jpeg, .png, .gif"
              multiple={newExamFileType === "images"}
              onChange={handleExamFileChange}
              className="w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-900 hover:file:bg-gray-200 mt-1 cursor-pointer"
              required
            />
            {newExamFiles.length > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Selected: {newExamFiles.length} {newExamFileType} file(s)
              </p>
            )}
          </div>
          <button
            type="submit"
            disabled={isCreatingExam || studentBatchesForExam.length === 0}
            className="bg-gray-900 text-white py-2.5 px-4 rounded-xl w-full hover:bg-gray-800 transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
          >
            {isCreatingExam ? (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              "Create Exam"
            )}
          </button>
        </form>
      </Modal>

      <Modal
        isOpen={isAssignExaminersToExamModalOpen}
        onClose={() => {
          setIsAssignExaminersToExamModalOpen(false);
          setAssignExaminerSearchTerm("");
        }}
        title="Assign Examiners to Exam Pool"
      >
        <div className="space-y-6 p-2">
          <div>
            <label
              htmlFor="selectExam"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Select Exam (Only unassigned exams shown)
            </label>
            <select
              id="selectExam"
              value={
                selectedExamForExaminerAssignment
                  ? selectedExamForExaminerAssignment._id
                  : ""
              }
              onChange={(e) =>
                setSelectedExamForExaminerAssignment(
                  exams.find((exam) => exam._id === e.target.value)
                )
              }
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              disabled={unassignedExamsForModal.length === 0}
            >
              <option value="">-- Choose an Exam --</option>
              {unassignedExamsForModal.map((exam) => (
                <option key={exam._id} value={exam._id}>
                  {exam.title} ({exam.totalPages || "N/A"} pages)
                </option>
              ))}
            </select>
            {unassignedExamsForModal.length === 0 && (
              <p className="text-sm text-gray-500 mt-1">
                All exams are currently assigned to examiners or no exams exist.
              </p>
            )}
          </div>
          {selectedExamForExaminerAssignment && (
            <div>
              <label
                htmlFor="selectExaminers"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Assign Examiners (Select multiple)
              </label>
              <input
                type="text"
                placeholder="Search by name or email..."
                value={assignExaminerSearchTerm}
                onChange={(e) => setAssignExaminerSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-blue-400 rounded-lg text-sm font-medium mb-2 focus:outline-none focus:border-blue-600"
              />
              <div className="border border-gray-200 rounded-xl p-3 max-h-40 overflow-y-auto bg-gray-50">
                {availableExaminers.filter(ex => `${ex.name} ${ex.email}`.toLowerCase().includes(assignExaminerSearchTerm.toLowerCase())).length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-2">
                    {availableExaminers.length === 0 ? "No examiners available." : "No matching examiners found."}
                  </p>
                ) : (
                  availableExaminers
                    .filter(ex => `${ex.name} ${ex.email}`.toLowerCase().includes(assignExaminerSearchTerm.toLowerCase()))
                    .map((examiner) => (
                    <div
                      key={examiner._id}
                      className="flex items-center mb-1 last:mb-0"
                    >
                      <input
                        type="checkbox"
                        id={`examiner-${examiner._id}`}
                        value={examiner._id}
                        checked={selectedExaminerIds.includes(examiner._id)}
                        onChange={() =>
                          handleExaminerCheckboxChange(examiner._id)
                        }
                        className="h-4 w-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 cursor-pointer"
                      />
                      <div className="ml-2 flex items-center justify-between w-full">
                        <label
                          htmlFor={`examiner-${examiner._id}`}
                          className="text-sm text-gray-700 cursor-pointer"
                        >
                          {examiner.name} ({examiner.email})
                        </label>
                        <div className="ml-3 flex items-center gap-2">
                          <span className="text-xs text-gray-500">{(examinerCopyCounts[examiner._id]?.total || 0)}/{(examinerCopyCounts[examiner._id]?.evaluated || 0)}</span>
                          {(examinerCopyCounts[examiner._id]?.total || 0) === 0 && (examinerCopyCounts[examiner._id]?.evaluated || 0) === 0 ? (
                            <button
                              onClick={() => handleUnlinkExaminer(examiner._id)}
                              disabled={unlinkingExaminerId === examiner._id}
                              className="text-xs text-red-600 hover:underline ml-2"
                            >
                              {unlinkingExaminerId === examiner._id ? 'Unlinking...' : 'Unlink'}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Hold Ctrl/Cmd to select multiple examiners.
              </p>
            </div>
          )}
          <button
            onClick={handleAssignExaminersToExam}
            disabled={
              !selectedExamForExaminerAssignment ||
              selectedExaminerIds.length === 0 ||
              isAssigning
            }
            className="bg-gray-900 text-white py-2.5 px-4 rounded-xl w-full disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 transition duration-200 flex items-center justify-center font-medium text-sm"
          >
            {isAssigning ? (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                Assigning...
              </>
            ) : (
              "Assign Examiners & Distribute Copies"
            )}
          </button>
        </div>
      </Modal>

      <ScanCopyUploadModal
        isOpen={isScanUploadModalOpen}
        onClose={() => setIsScanUploadModalOpen(false)}
        onUploadSuccess={handleScanUploadSuccess}
        questionPapers={exams}
        students={users.filter((u) => u.role === "student")}
        copies={copiesForModals}
      />

      <ExamReportGenerator
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setSelectedExamForReport(null);
          setCopiesForModals([]);
        }}
        exam={selectedExamForReport}
        copies={copiesForModals}
        users={users}
      />

      <Modal
        isOpen={isEditExamModalOpen}
        onClose={() => {
          setIsEditExamModalOpen(false);
          setSelectedExamForEdit(null);
          setEditExamTitle("");
        }}
        title="Edit Exam Title"
      >
        <div className="space-y-4 p-2">
          <div>
            <label
              htmlFor="currentExamTitle"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Current Exam Title
            </label>
            <input
              type="text"
              id="currentExamTitle"
              value={selectedExamForEdit?.title || ""}
              disabled
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 text-sm cursor-not-allowed"
            />
          </div>

          <div>
            <label
              htmlFor="newExamTitle"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              New Exam Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="newExamTitle"
              value={editExamTitle}
              onChange={(e) => setEditExamTitle(e.target.value)}
              placeholder="Enter new exam title"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              autoFocus
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isUpdatingExam) {
                  handleUpdateExamTitle();
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Changing the exam title will not affect any copies, queries, or examiner assignments.
            </p>
          </div>

          <div>
            <label
              htmlFor="newExamTotalMarks"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Total Marks <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="newExamTotalMarks"
              value={editExamTotalMarks}
              onChange={(e) => setEditExamTotalMarks(e.target.value)}
              placeholder="Enter total marks"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
              min="1"
              max="10000"
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isUpdatingExam) {
                  handleUpdateExamTitle();
                }
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              Note: Changing total marks will not affect copies already assigned to examiners.
            </p>
          </div>

          {selectedExamForEdit && (selectedExamForEdit.assignedExaminers || []).length > 0 && (
            <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
              <h3 className="text-sm font-medium text-gray-800 mb-2">Assigned Examiners</h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {selectedExamForEdit.assignedExaminers.map((examiner) => {
                  const id = examiner._id || examiner;
                  const counts = examinerCopyCounts[id] || { total: 0, evaluated: 0 };
                  const canUnlink = counts.total === 0 && counts.evaluated === 0;
                  return (
                    <div key={id} className="flex items-center justify-between">
                      <div className="text-sm text-gray-700">{examiner.name || examiner.email} <span className="text-xs text-gray-500">({examiner.email})</span></div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500">{counts.total}/{counts.evaluated}</span>
                        {canUnlink ? (
                          <button
                            onClick={() => handleUnlinkExaminer(id)}
                            disabled={unlinkingExaminerId === id}
                            className="text-xs text-red-600 hover:underline"
                          >
                            {unlinkingExaminerId === id ? 'Unlinking...' : 'Unlink'}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Cannot unlink</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2">Only examiners with 0 assigned copies can be unlinked here.</p>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              onClick={() => {
                setIsEditExamModalOpen(false);
                setSelectedExamForEdit(null);
                setEditExamTitle("");
                setEditExamTotalMarks("");
              }}
              disabled={isUpdatingExam}
              className="flex-1 bg-gray-200 text-gray-700 py-2.5 px-4 rounded-xl hover:bg-gray-300 transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateExamTitle}
              disabled={isUpdatingExam || !editExamTitle.trim() || !editExamTotalMarks || parseInt(editExamTotalMarks, 10) <= 0}
              className="flex-1 bg-[#1e3a8a] text-white py-2.5 px-4 rounded-xl hover:bg-[#1e40af] transition duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center font-medium text-sm"
            >
              {isUpdatingExam ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <PencilSquareIcon className="h-5 w-5 mr-2" />
                  Update Details
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Examiner details moved to /admin/examiners page */}
    </div>
  );
}
